import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { format, subDays, parseISO, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter } from "date-fns";
import { nl } from "date-fns/locale";
import { BarChart3, Sparkles, Brain, Activity, AlertTriangle, Printer, Mail } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, ComposedChart, Line, Legend } from "recharts";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

export default function LoadReport() {
  const [start, setStart] = useState(format(subDays(new Date(), 29), "yyyy-MM-dd"));
  const [end, setEnd] = useState(format(new Date(), "yyyy-MM-dd"));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [focus, setFocus] = useState("workload");
  const [tip, setTip] = useState("");
  const [tipLoading, setTipLoading] = useState(false);
  const [emailing, setEmailing] = useState(false);

  const loadReport = useCallback(() => {
    setLoading(true);
    api.get("/admin/load-report", { params: { start, end } })
      .then((r) => setReport(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [start, end]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const genTip = async () => {
    setTipLoading(true); setTip("");
    try {
      const r = await api.post("/admin/load-report/tip", { start, end, focus });
      setTip(r.data.tip || "");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTipLoading(false); }
  };

  const days = report?.days || [];
  const withData = days.filter((d) => d.load > 0 || d.summary);
  const overDays = days.filter((d) => d.max > 0 && d.load > d.max).length;
  const chartData = days.map((d) => ({ label: format(parseISO(d.date), "d/M"), load: d.load, max: d.max, over: d.max > 0 && d.load > d.max, felt_workload: d.felt_workload || null, felt_mental: d.felt_mental || null }));

  const emailReport = async () => {
    setEmailing(true);
    try {
      const r = await api.post("/admin/load-report/email", { start, end, tip });
      toast.success(`Rapport gemaild naar ${r.data.to}.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setEmailing(false); }
  };

  const applyPreset = (k) => {
    const now = new Date();
    let s, e;
    if (k === "week") { s = startOfWeek(now, { weekStartsOn: 1 }); e = endOfWeek(now, { weekStartsOn: 1 }); }
    else if (k === "month") { s = startOfMonth(now); e = endOfMonth(now); }
    else { s = startOfQuarter(now); e = endOfQuarter(now); }
    setStart(format(s, "yyyy-MM-dd")); setEnd(format(e, "yyyy-MM-dd"));
  };

  return (
    <div className="max-w-4xl" id="report-print">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-medium tracking-tight mb-1 flex items-center gap-2"><BarChart3 className="w-7 h-7 text-brand" strokeWidth={1.6} /> Belastingsrapport</h1>
          <p className="text-sm text-ink-muted mb-6">Bekijk per dag je belastingscore en dagsamenvatting over een periode, met een AI-tip om beter te functioneren.</p>
        </div>
        <div className="flex gap-2 no-print">
          <Button variant="outline" onClick={() => window.print()} data-testid="print-report" className="rounded-full border-[#E8E2D9]"><Printer className="w-4 h-4 mr-2" /> Printen / PDF</Button>
          <Button variant="outline" onClick={emailReport} disabled={emailing} data-testid="email-report" className="rounded-full border-[#E8E2D9]"><Mail className="w-4 h-4 mr-2" /> {emailing ? "Versturen…" : "Mailen"}</Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-5 shadow-[0_8px_30px_rgb(74,63,63,0.04)] mb-5">
        <div className="flex flex-wrap gap-2 mb-4 no-print" data-testid="report-presets">
          {[{ k: "week", label: "Deze week" }, { k: "month", label: "Deze maand" }, { k: "quarter", label: "Dit kwartaal" }].map((p) => (
            <button key={p.k} type="button" data-testid={`preset-${p.k}`} onClick={() => applyPreset(p.k)}
              className="px-3.5 py-1.5 rounded-full text-sm border border-[#E8E2D9] bg-white text-ink hover:border-brand hover:text-brand transition-colors">{p.label}</button>
          ))}
        </div>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <Label className="text-sm text-ink-muted mb-1.5 block">Van</Label>
            <Input type="date" data-testid="report-start" value={start} max={end} onChange={(e) => setStart(e.target.value)} className="rounded-xl border-[#E8E2D9] w-44" />
          </div>
          <div>
            <Label className="text-sm text-ink-muted mb-1.5 block">Tot en met</Label>
            <Input type="date" data-testid="report-end" value={end} min={start} onChange={(e) => setEnd(e.target.value)} className="rounded-xl border-[#E8E2D9] w-44" />
          </div>
          <div className="flex-1" />
          {report && (
            <div className="flex gap-5 text-sm">
              <div><div className="text-ink-muted text-xs">Totaal</div><div className="text-2xl font-display" data-testid="report-total">{report.total}</div></div>
              <div><div className="text-ink-muted text-xs">Gemiddeld/dag</div><div className="text-2xl font-display">{report.avg}</div></div>
              <div><div className="text-ink-muted text-xs">Overvolle dagen</div><div className="text-2xl font-display">{overDays}</div></div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-5 shadow-[0_8px_30px_rgb(74,63,63,0.04)] mb-5">
        <h2 className="font-medium mb-3">Per dag</h2>
        {!loading && withData.length > 0 && (
          <div className="h-60 -ml-3 mb-5" data-testid="load-chart">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 6, right: 4, left: -20, bottom: 0 }}>
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#9b8f86" }} interval="preserveStartEnd" tickLine={false} axisLine={{ stroke: "#E8E2D9" }} />
                <YAxis yAxisId="load" tick={{ fontSize: 10, fill: "#9b8f86" }} allowDecimals={false} tickLine={false} axisLine={false} width={28} />
                <YAxis yAxisId="felt" orientation="right" domain={[0, 5]} ticks={[1, 2, 3, 4, 5]} tick={{ fontSize: 10, fill: "#9b8f86" }} tickLine={false} axisLine={false} width={20} />
                <Tooltip cursor={{ fill: "rgba(183,138,110,0.08)" }} contentStyle={{ borderRadius: 12, border: "1px solid #E8E2D9", fontSize: 12 }}
                  formatter={(v, n, p) => n === "Belasting" ? [`${v}/${p.payload.max}`, n] : [`${v}/5`, n]} />
                <Legend wrapperStyle={{ fontSize: 11 }} iconType="plainline" />
                <Bar yAxisId="load" dataKey="load" name="Belasting" radius={[4, 4, 0, 0]} maxBarSize={22}>
                  {chartData.map((e, i) => <Cell key={i} fill={e.over ? "#C2607A" : "#B78A6E"} />)}
                </Bar>
                <Line yAxisId="felt" type="monotone" dataKey="felt_workload" name="Ervaren werkbelasting" stroke="#C99A3A" strokeWidth={2} dot={{ r: 2 }} connectNulls />
                <Line yAxisId="felt" type="monotone" dataKey="felt_mental" name="Ervaren mentaal" stroke="#7E6BA8" strokeWidth={2} dot={{ r: 2 }} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
        {loading ? (
          <p className="text-sm text-ink-muted py-6 text-center">Rapport laden…</p>
        ) : withData.length === 0 ? (
          <p className="text-sm text-ink-muted py-6 text-center" data-testid="report-empty">Geen belasting of notities in deze periode.</p>
        ) : (
          <div className="space-y-1.5">
            {withData.map((d) => {
              const pct = d.max > 0 ? Math.min(100, Math.round((d.load / d.max) * 100)) : 0;
              const over = d.max > 0 && d.load > d.max;
              const col = over ? "#C2607A" : d.load >= d.max * 0.75 ? "#E0BE73" : "#B78A6E";
              return (
                <div key={d.date} data-testid={`report-day-${d.date}`} className="flex items-start gap-3 py-2 border-b border-[#F2EBE1] last:border-0">
                  <div className="w-28 shrink-0 text-sm capitalize">{format(parseISO(d.date), "EEE d MMM", { locale: nl })}</div>
                  <div className="w-40 shrink-0 pt-1">
                    <div className="flex items-center gap-1.5">
                      {over && <AlertTriangle className="w-3 h-3 shrink-0 text-[#C2607A]" />}
                      <div className="flex-1 h-1.5 rounded-full bg-[#EFEAE2] overflow-hidden"><div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: col }} /></div>
                      <span className={`text-xs tabular-nums ${over ? "text-[#C2607A] font-semibold" : "text-ink-muted"}`}>{d.load}/{d.max}</span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                    {(d.felt_workload > 0 || d.felt_mental > 0) && (
                      <span className="inline-flex items-center gap-1.5 text-xs shrink-0" data-testid={`report-felt-${d.date}`}>
                        {d.felt_workload > 0 && <span className="px-1.5 py-0.5 rounded-md bg-[#F3ECE3] text-[#8a6a2f]" title="Ervaren werkbelasting">W {d.felt_workload}/5</span>}
                        {d.felt_mental > 0 && <span className="px-1.5 py-0.5 rounded-md bg-[#EEE9F3] text-[#6b5b8a]" title="Ervaren mentale belasting">M {d.felt_mental}/5</span>}
                      </span>
                    )}
                    <span className="text-sm text-ink-muted italic truncate">{d.summary ? `"${d.summary}"` : ""}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-5 shadow-[0_8px_30px_rgb(74,63,63,0.04)]">
        <h2 className="font-medium mb-1 flex items-center gap-2"><Sparkles className="w-5 h-5 text-brand" /> AI-tip</h2>
        <p className="text-sm text-ink-muted mb-3">Waar wil je op focussen? De tip kijkt naar je belasting én hoe je de dagen hebt ervaren.</p>
        <div className="flex flex-wrap items-center gap-2 mb-4 no-print">
          <button type="button" data-testid="focus-workload" onClick={() => setFocus("workload")}
            className={`px-4 py-2 rounded-full text-sm border transition-colors duration-200 inline-flex items-center gap-1.5 ${focus === "workload" ? "bg-brand text-white border-brand" : "bg-white text-ink border-[#E8E2D9] hover:border-brand"}`}>
            <Activity className="w-4 h-4" /> Werkdruk
          </button>
          <button type="button" data-testid="focus-mental" onClick={() => setFocus("mental")}
            className={`px-4 py-2 rounded-full text-sm border transition-colors duration-200 inline-flex items-center gap-1.5 ${focus === "mental" ? "bg-brand text-white border-brand" : "bg-white text-ink border-[#E8E2D9] hover:border-brand"}`}>
            <Brain className="w-4 h-4" /> Mentaal
          </button>
          <div className="flex-1" />
          <Button onClick={genTip} disabled={tipLoading} data-testid="generate-tip" className="rounded-full bg-brand hover:bg-brand-hover text-white">
            {tipLoading ? "Bezig…" : "Genereer tip"}
          </Button>
        </div>
        {tip && (
          <div data-testid="ai-tip" className="rounded-xl bg-cream-subtle/60 border border-[#E8E2D9] px-4 py-3.5 text-sm leading-relaxed whitespace-pre-line">{tip}</div>
        )}
      </div>
    </div>
  );
}
